import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Button, Typography, Alert, Link, Paper } from '@mui/material';
import { Form, Formik } from 'formik';
import { toValidator } from 'renderer/shared/schemas/common.schemas';
import { formatQueryError } from 'renderer/shared/formatters/formatters';
import {
  useLoginMutation,
  useSendCodeMutation,
} from '../services/auth.endpoints';
import {
  EnrollFormSchema,
  LoginFormSchema,
  VerifyCodeFormSchema,
} from '../schemas/auth.schemas';
import Credentials from './Credentials';
import EnrollForm from './EnrollForm';
import SendCode from './SendCode';
import VerifyCodeForm from './VerifyCode';

type Steps = 'credentials' | 'enroll' | 'send' | 'verify' | 'success';

const initialForm = {
  email: '',
  password: '',
  phone: '',
  code: '',
};

const Login: React.FC = () => {
  const [
    login,
    { isLoading: isLoginLoading, error: loginError, data: loginResult },
  ] = useLoginMutation();
  const [
    sendCode,
    {
      isLoading: isSendCodeLoading,
      error: sendCodeError,
      isSuccess: isSendCodeSuccess,
    },
  ] = useSendCodeMutation();

  const [step, setStep] = useState<Steps>('credentials');

  const navigate = useNavigate();

  const isLoading = isLoginLoading || isSendCodeLoading;

  const phoneToSend =
    loginResult && loginResult.security ? loginResult.security.sms : 'XXXX';

  useEffect(() => {
    if (!loginResult) return;
    if (loginResult.user) {
      setStep('success');
      navigate('/');
      return;
    }
    if (!loginResult.security) return;
    if (loginResult.security.sms === 'MISSING') {
      setStep('enroll');
      return;
    }
    setStep('send');
  }, [loginResult, navigate]);

  useEffect(() => {
    if (isSendCodeSuccess) {
      setStep('verify');
    }
  }, [isSendCodeSuccess]);

  const handleCancel = () => {
    navigate('/');
    // resetForm();
  };

  const handleSubmit = (values: typeof initialForm) => {
    login(values);
  };

  const requestCode = (values: typeof initialForm) => {
    sendCode({ ...values, username: values.email });
  };

  const validate = (values: typeof initialForm) => {
    switch (step) {
      case 'credentials':
        return toValidator(LoginFormSchema)(values);
      case 'enroll':
        return toValidator(EnrollFormSchema)(values);
      case 'verify':
        return toValidator(VerifyCodeFormSchema)(values);
      default:
        return {};
    }
  };

  return (
    <Paper sx={{ padding: 5, maxWidth: '40%', margin: '20px auto' }}>
      <Typography
        variant="h4"
        component="h1"
        gutterBottom
        sx={{ textAlign: 'center' }}
      >
        Login
      </Typography>
      {(loginError || sendCodeError) && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {loginError && formatQueryError(loginError)}
          {sendCodeError && formatQueryError(sendCodeError)}
        </Alert>
      )}
      <Formik
        initialValues={initialForm}
        onSubmit={handleSubmit}
        validate={validate}
      >
        {({ values }) => (
          <Form>
            {step === 'credentials' && <Credentials disabled={isLoading} />}
            {step === 'enroll' && (
              <EnrollForm
                disabled={isLoading}
                sendCode={() => requestCode(values)}
              />
            )}
            {step === 'send' && (
              <SendCode
                disabled={isLoading}
                phone={phoneToSend}
                sendCode={() => requestCode(values)}
              />
            )}
            {step === 'verify' && (
              <VerifyCodeForm
                disabled={isLoading}
                resendCode={() => requestCode(values)}
              />
            )}
          </Form>
        )}
      </Formik>
      <Button
        onClick={handleCancel}
        variant="outlined"
        fullWidth
        sx={{ mt: 2 }}
      >
        Cancel
      </Button>
      <Typography variant="body2" sx={{ mt: 2, textAlign: 'center' }}>
        <Link component={RouterLink} to="/forgot-password">
          Forgot Password?
        </Link>
      </Typography>
      <Typography variant="body2" sx={{ mt: 2, textAlign: 'center' }}>
        Not registered?{' '}
        <Link component={RouterLink} to="/register">
          Create an account
        </Link>
      </Typography>
    </Paper>
  );
};

export default Login;
